import { useEffect, useState, useMemo } from 'react';
import { collection, collectionGroup, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { useNavigate, NavLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useMarket, filterByPortals, isAllPortals } from '../../contexts/MarketContext';
import useBasePath from '../../utils/useBasePath';
import { useT } from '../../contexts/LocaleContext';
import {
  TOUR_CATEGORIES, CATEGORY_LABELS, CATEGORY_STYLES, CATEGORY_DOT,
  CATEGORY_COLORS, CATEGORY_FILLS, CATEGORY_SHORT,
  isCategoryComplete,
} from '../../utils/statuses';
import useStatusPipeline from '../../hooks/useStatusPipeline';
import { Sparkline, TrendChip, RevenueChart, PipelineFunnel, WorkloadBar, ProgressRing } from '../../components/charts';
import SportIcon from '../../components/SportIcon';
import { totalTravellers } from '../../utils/groupSize';

const ACTIVE_CATEGORIES = ['sales_admin', 'tour_preparation', 'leader_handover', 'leader_signoff', 'post_tour'];
const IN_PROGRESS_CATEGORIES = ['tour_preparation', 'leader_handover', 'leader_signoff'];

// A post_tour tour whose checklist (feedback form, accounts, social posts) is
// fully ticked is done — it shouldn't still read as "active" or "upcoming".
function isTourCompleted(r, stagesFor) {
  return (r.category || 'sales_admin') === 'post_tour'
    && isCategoryComplete('post_tour', r.stageProgress, stagesFor('post_tour', r));
}
const CURRENCY_SYMBOLS = { GBP: '£', EUR: '€', USD: '$' };
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function formatDate(str) {
  if (!str) return '—';
  const [y, m, d] = str.split('-');
  return `${d}/${m}/${y}`;
}

function fmtMoneyShort(n, sym = '£') {
  if (n == null || n === 0) return `${sym}0`;
  const v = Number(n);
  if (v >= 1e6) return `${sym}${(v / 1e6).toFixed(1)}m`;
  if (v >= 1e3) return `${sym}${(v / 1e3).toFixed(1)}k`;
  return `${sym}${v.toFixed(0)}`;
}

function fmtMoney(n, sym = '£') {
  if (n == null) return '—';
  return `${sym}${Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function daysUntil(str) {
  if (!str) return null;
  const [y, m, d] = str.split('-').map(Number);
  const today = new Date(); today.setHours(0,0,0,0);
  return Math.round((new Date(y, m - 1, d) - today) / 864e5);
}

function fmtShortDate(str) {
  if (!str) return '—';
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function getDateLine() {
  return new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

/* ── Small reusable pieces ── */

function CategoryChip({ catKey }) {
  return (
    <span className={`inline-flex items-center gap-1 px-[9px] py-[3px] rounded-[20px] text-[11px] font-bold whitespace-nowrap flex-shrink-0 ${CATEGORY_STYLES[catKey] || 'bg-[#F3F4F6] text-[#6B7280]'}`}>
      <span className={`w-[5px] h-[5px] rounded-full flex-shrink-0 ${CATEGORY_DOT[catKey] || 'bg-[#9CA3AF]'}`} />
      {CATEGORY_LABELS[catKey] || catKey}
    </span>
  );
}

function Card({ title, sub, action, children, noPad }) {
  return (
    <section className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] overflow-hidden">
      {(title || action) && (
        <header className="flex items-start justify-between gap-3 px-[18px] pt-[16px] pb-[12px] border-b border-[#F1F3F6]">
          <div>
            <h3 className="text-[14.5px] font-bold text-[#111827]">{title}</h3>
            {sub && <p className="text-[12px] text-[#6B7280] mt-0.5">{sub}</p>}
          </div>
          {action}
        </header>
      )}
      <div className={noPad ? '' : 'px-[18px] pb-[18px] pt-[6px]'}>{children}</div>
    </section>
  );
}

function ViewAll({ children, to, onClick }) {
  const t = useT();
  const inner = (
    <span className="text-[12.5px] font-bold text-pb hover:text-navy transition whitespace-nowrap flex-shrink-0">
      {children || t('dashboard.viewAll')} →
    </span>
  );
  if (to) return <NavLink to={to}>{inner}</NavLink>;
  return <button onClick={onClick}>{inner}</button>;
}

/* ── KPI card ── */
function KpiCard({ label, value, sub, sparkData, sparkColor, trend, alert, to }) {
  const inner = (
    <div className={`rounded-[14px] border p-[16px] shadow-[0_1px_2px_rgba(16,24,40,.04)] hover:shadow-[0_6px_18px_rgba(16,24,40,.08)] hover:-translate-y-px transition-all ${
      alert ? 'bg-[#FFF8F7] border-[#F3C6C1]' : 'bg-white border-[#E5E7EB]'
    } ${to ? 'cursor-pointer' : ''}`}>
      <div className="flex items-start justify-between gap-2">
        <span className="text-[12px] font-semibold text-[#6B7280] whitespace-nowrap">{label}</span>
        {trend && <TrendChip dir={trend.dir} value={trend.value} muted={alert} />}
      </div>
      <div className="flex items-end justify-between mt-[10px] gap-2">
        <div>
          <div className={`text-[30px] font-extrabold leading-none tracking-[-1px] tabular-nums ${alert ? 'text-dr' : 'text-navy'}`}>
            {value}
          </div>
          <div className="text-[11.5px] mt-1" style={{ color: alert ? 'rgba(192,57,43,.7)' : '#9CA3AF' }}>{sub}</div>
        </div>
        {sparkData && <Sparkline data={sparkData} color={sparkColor} />}
      </div>
    </div>
  );
  if (to) return <NavLink to={to}>{inner}</NavLink>;
  return inner;
}

/* ── Segmented control ── */
function Segment({ options, value, onChange, labelFor }) {
  return (
    <div className="flex items-center bg-white border border-[#E5E7EB] rounded-[9px] p-[3px] gap-[2px]">
      {options.map((opt) => (
        <button
          key={opt}
          onClick={() => onChange(opt)}
          className={`px-3 py-[5px] rounded-[7px] text-[12px] font-semibold transition ${
            value === opt ? 'bg-navy text-white' : 'text-[#6B7280] hover:text-[#374151]'
          }`}
        >
          {labelFor ? labelFor(opt) : opt}
        </button>
      ))}
    </div>
  );
}

export default function DashboardHome() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { portals } = useMarket();
  const { stagesFor } = useStatusPipeline();
  const base = useBasePath();
  const t = useT();
  const [requests, setRequests] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(true);
  const [tasksError, setTasksError] = useState(false);
  const [period, setPeriod] = useState('Year');

  const firstName = currentUser?.displayName?.split(' ')[0] || currentUser?.email?.split('@')[0] || 'there';
  const greetKey = (() => {
    const h = new Date().getHours();
    return h < 12 ? 'dashboard.greetingMorning' : h < 18 ? 'dashboard.greetingAfternoon' : 'dashboard.greetingEvening';
  })();

  useEffect(() => {
    const unsub = onSnapshot(query(collection(db, 'requests')), (snap) => {
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
        .filter((r) => !r.crmOrigin || r.crmFullyConverted);
      setRequests(filterByPortals(docs, portals));
    });
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portals.join(',')]);

  useEffect(() => {
    const q = query(collection(db, 'invoices'), orderBy('dueDate', 'asc'));
    const unsub = onSnapshot(q, (snap) => {
      setInvoices(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, []);

  // Only surface invoices whose parent tour is in the active scope. `requests`
  // is already scope-filtered, so membership enforces market + brand. When the
  // scope is fully unrestricted (admin "All Tours"), show every invoice.
  const marketRequestIds = useMemo(() => new Set(requests.map((r) => r.id)), [requests]);
  const marketInvoices = useMemo(() => {
    return isAllPortals(portals) ? invoices : invoices.filter((i) => marketRequestIds.has(i.requestId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices, marketRequestIds, portals.join(',')]);

  useEffect(() => {
    const q = query(collectionGroup(db, 'tasks'), where('completed', '==', false));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setTasks(snap.docs.map((d) => ({ id: d.id, requestId: d.ref.parent.parent.id, ...d.data() })));
        setLoadingTasks(false);
      },
      (err) => { console.error('Tasks collectionGroup error:', err); setTasksError(true); setLoadingTasks(false); }
    );
    return unsub;
  }, []);

  // ── Derived data ────────────────────────────────────────────────────────────
  const today = useMemo(() => { const d = new Date(); d.setHours(0,0,0,0); return d; }, []);

  const activeRequests = useMemo(
    () => requests.filter((r) => ACTIVE_CATEGORIES.includes(r.category || 'sales_admin') && !isTourCompleted(r, stagesFor)),
    [requests, stagesFor]
  );

  const completedTours = useMemo(() => requests.filter((r) => isTourCompleted(r, stagesFor)), [requests, stagesFor]);

  const skeletalPendingApprovals = useMemo(
    () => currentUser?.email === 'info@mstsportstours.com'
      ? requests.filter((r) => r.skeletalApprovalState === 'pending_admin')
      : [],
    [requests, currentUser]
  );

  const activeRequestIds = useMemo(() => new Set(activeRequests.map((r) => r.id)), [activeRequests]);

  const toursInProgress = useMemo(
    () => requests
      .filter((r) => IN_PROGRESS_CATEGORIES.includes(r.category || 'sales_admin'))
      .sort((a, b) => (a.departureDate || '').localeCompare(b.departureDate || '')),
    [requests]
  );

  const pendingReview = useMemo(
    () => requests.filter((r) => (r.category || 'sales_admin') === 'sales_admin' && !r.stageProgress?.initial_interest),
    [requests]
  );

  const upcomingDepartures = useMemo(() => {
    const in30Days = new Date(today); in30Days.setDate(in30Days.getDate() + 30);
    return requests
      .filter((r) => {
        if (!r.departureDate || ['cancelled', 'post_tour'].includes(r.category)) return false;
        const dep = new Date(r.departureDate);
        return dep >= today && dep <= in30Days;
      })
      .sort((a, b) => (a.departureDate || '').localeCompare(b.departureDate || ''));
  }, [requests, today]);

  const requestsById = useMemo(() => Object.fromEntries(requests.map((r) => [r.id, r])), [requests]);

  const { unpaidInvoices, overdueInvoices, outstandingGBP } = useMemo(() => {
    const unpaid = marketInvoices.filter((i) => i.status === 'unpaid');
    const overdue = unpaid.filter((i) => i.dueDate && new Date(i.dueDate) < today);
    const gbp = unpaid.reduce((sum, i) => {
      const amt = Number(i.amount) || 0;
      const rate = i.currency === 'EUR' ? 0.86 : i.currency === 'USD' ? 0.79 : 1;
      return sum + amt * rate;
    }, 0);
    return { unpaidInvoices: unpaid, overdueInvoices: overdue, outstandingGBP: gbp };
  }, [marketInvoices, today]);

  const { requestsWithTasks, totalIncompleteTasks } = useMemo(() => {
    const byRequest = {};
    tasks.filter((t) => activeRequestIds.has(t.requestId)).forEach((t) => {
      if (!byRequest[t.requestId]) byRequest[t.requestId] = [];
      byRequest[t.requestId].push(t);
    });
    const withTasks = Object.entries(byRequest)
      .map(([requestId, taskList]) => ({ requestId, request: requests.find((r) => r.id === requestId), tasks: taskList }))
      .filter((e) => e.request)
      .sort((a, b) => b.tasks.length - a.tasks.length);
    const total = Object.values(byRequest).reduce((n, arr) => n + arr.length, 0);
    return { requestsWithTasks: withTasks, totalIncompleteTasks: total };
  }, [tasks, activeRequestIds, requests]);

  const pipelineData = useMemo(() => {
    return TOUR_CATEGORIES.map((cat) => {
      const catReqs = activeRequests.filter((r) => (r.category || 'sales_admin') === cat);
      const value = catReqs.reduce((s, r) => s + (Number(r.quoteDetails?.totalCostToCustomer) || 0), 0);
      return { key: cat, count: catReqs.length, value };
    }).filter((d) => d.count > 0);
  }, [activeRequests]);

  const workloadData = useMemo(() => {
    const counts = {};
    tasks.filter((t) => activeRequestIds.has(t.requestId)).forEach((t) => {
      const req = requests.find((r) => r.id === t.requestId);
      const cat = req?.category || 'sales_admin';
      counts[cat] = (counts[cat] || 0) + 1;
    });
    return TOUR_CATEGORIES.map((key) => ({ key, count: counts[key] || 0 })).filter((d) => d.count > 0);
  }, [tasks, activeRequestIds, requests]);

  const revenueData = useMemo(() => {
    const year = new Date().getFullYear();
    const curMonth = new Date().getMonth();
    const count = Math.min(curMonth + 2, 8);
    return MONTHS.slice(0, count).map((m, idx) => {
      const monthInvs = marketInvoices.filter((inv) => {
        if (!inv.dueDate) return false;
        const d = new Date(inv.dueDate);
        return d.getFullYear() === year && d.getMonth() === idx;
      });
      const toK = (inv) => {
        const amt = Number(inv.amount) || 0;
        const r = inv.currency === 'EUR' ? 0.86 : inv.currency === 'USD' ? 0.79 : 1;
        return (amt * r) / 1000;
      };
      const invoiced = Math.round(monthInvs.reduce((s, i) => s + toK(i), 0));
      const collected = Math.round(monthInvs.filter((i) => i.status === 'paid').reduce((s, i) => s + toK(i), 0));
      return { m, invoiced, collected, future: idx > curMonth };
    });
  }, [marketInvoices]);

  // Urgency
  const urgentItems = [];
  if (pendingReview.length > 0) urgentItems.push(`${pendingReview.length} tour${pendingReview.length !== 1 ? 's' : ''} awaiting review`);
  if (overdueInvoices.length > 0) urgentItems.push(`${overdueInvoices.length} overdue invoice${overdueInvoices.length !== 1 ? 's' : ''}`);

  // Spark data — computed from real Firestore records
  const sparks = useMemo(() => {
    const WEEK = 7 * 24 * 60 * 60 * 1000;
    const now = Date.now();

    function tsOf(r) {
      const t = r.createdAt;
      if (!t) return 0;
      if (typeof t.toDate === 'function') return t.toDate().getTime();
      if (t.seconds) return t.seconds * 1000;
      return new Date(t).getTime();
    }

    // 8 weekly buckets oldest→newest (last 8 weeks)
    const weeks = Array.from({ length: 8 }, (_, i) => ({
      start: now - (8 - i) * WEEK,
      end:   now - (7 - i) * WEEK,
    }));

    // Active Tours: in-progress requests created each week
    const active = weeks.map(({ start, end }) =>
      requests.filter((r) => {
        const ts = tsOf(r);
        return IN_PROGRESS_CATEGORIES.includes(r.category || 'sales_admin') && ts >= start && ts < end;
      }).length
    );

    // Awaiting Review: unreviewed sales_admin requests created each week
    const awaiting = weeks.map(({ start, end }) =>
      requests.filter((r) => {
        const ts = tsOf(r);
        return (r.category || 'sales_admin') === 'sales_admin'
          && !r.stageProgress?.initial_interest
          && ts >= start && ts < end;
      }).length
    );

    // Departures: tours departing in each of the next 8 weeks
    const departs = Array.from({ length: 8 }, (_, i) => {
      const start = now + i * WEEK;
      const end = start + WEEK;
      return requests.filter((r) => {
        if (!r.departureDate || r.category === 'cancelled') return false;
        const dep = new Date(r.departureDate).getTime();
        return dep >= start && dep < end;
      }).length;
    });

    // Outstanding: total unpaid invoice value (GBP) due per month, last 8 months
    const outstanding = Array.from({ length: 8 }, (_, i) => {
      const d = new Date(); d.setDate(1);
      d.setMonth(d.getMonth() - (7 - i));
      const start = d.getTime();
      const endD = new Date(d); endD.setMonth(endD.getMonth() + 1);
      const end = endD.getTime();
      return Math.round(
        marketInvoices
          .filter((inv) => {
            if (!inv.dueDate) return false;
            const t = new Date(inv.dueDate).getTime();
            return t >= start && t < end;
          })
          .reduce((sum, inv) => {
            const amt = Number(inv.amount) || 0;
            const rate = inv.currency === 'EUR' ? 0.86 : inv.currency === 'USD' ? 0.79 : 1;
            return sum + (amt * rate) / 1000;
          }, 0)
      );
    });

    // Ensure the sparkline always has at least two distinct values so the SVG
    // path renders. If all buckets are zero, hold a flat line at 0.1.
    const safe = (arr) => arr.every((v) => v === 0) ? arr.map(() => 0.1) : arr;

    return {
      active:      safe(active),
      awaiting:    safe(awaiting),
      departs:     safe(departs),
      outstanding: safe(outstanding),
    };
  }, [requests, marketInvoices]);

  return (
    <div className="space-y-[18px]">

      {/* ── Page header ── */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-[23px] font-extrabold text-navy tracking-[-0.4px]">
            {t(greetKey)}, {firstName}
          </h1>
          <p className="text-[13px] text-[#6B7280] mt-1">
            {getDateLine()} · {t('dashboard.headerSub')}
          </p>
        </div>
        <div className="flex items-center gap-[10px] flex-shrink-0">
          <Segment options={['Today','Week','Month']} value={period} onChange={setPeriod} labelFor={(o) => t(`dashboard.period${o}`)} />
          <NavLink
            to={`${base}/requests/new`}
            className="flex items-center gap-1.5 bg-pb hover:bg-navy text-white text-[13px] font-bold px-[16px] py-[10px] rounded-[9px] transition shadow-sm whitespace-nowrap"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
              <path d="M12 5v14M5 12h14" />
            </svg>
            {t('common.newTour')}
          </NavLink>
        </div>
      </div>

      {/* ── Skeletal itinerary approval banner ── */}
      {skeletalPendingApprovals.length > 0 && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-[12px] border animate-pulse-once"
          style={{ background: '#EFF6FF', borderColor: 'rgba(26,74,138,0.3)' }}>
          <span className="w-8 h-8 rounded-[8px] flex items-center justify-center flex-shrink-0 bg-pb/10">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1A4A8A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-bold text-pb">
              {skeletalPendingApprovals.length === 1 ? '1 skeletal itinerary' : `${skeletalPendingApprovals.length} skeletal itineraries`} awaiting your approval
            </div>
            <div className="text-[12px] text-[#2563EB]/80 truncate">
              {skeletalPendingApprovals.map((r) => r.internalRef || r.tourName || `${r.sport} — ${r.destination}`).join(' · ')}
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            {skeletalPendingApprovals.map((r) => (
              <button
                key={r.id}
                onClick={() => navigate(`${base}/requests/${r.id}#itinerary`)}
                className="text-[12px] font-bold text-pb px-3 py-1.5 rounded-[8px] transition whitespace-nowrap"
                style={{ background: 'rgba(26,74,138,0.12)' }}
              >
                {skeletalPendingApprovals.length === 1 ? 'Review itinerary →' : (r.internalRef || r.tourName || r.sport || r.id)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Action banner ── */}
      {urgentItems.length > 0 && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-[12px] border"
          style={{ background: '#FEF6E4', borderColor: 'rgba(232,160,32,.3)' }}>
          <span className="w-8 h-8 rounded-[8px] flex items-center justify-center flex-shrink-0"
            style={{ background: 'rgba(232,160,32,.18)' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#92400E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 9v3.75m-9.3 3.4c-.87 1.5.22 3.37 1.95 3.37h14.7c1.73 0 2.82-1.87 1.95-3.37L13.95 3.38c-.87-1.5-3.03-1.5-3.9 0z M12 15.75h.01" />
            </svg>
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-bold text-[#92400E]">{t('dashboard.actionRequired')}</div>
            <div className="text-[12px] text-[#92400E]/80">{urgentItems.join(' · ')}</div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            {pendingReview.length > 0 && (
              <NavLink to={`${base}/requests`}
                className="text-[12px] font-bold text-[#92400E] px-3 py-1.5 rounded-[8px] transition whitespace-nowrap"
                style={{ background: 'rgba(232,160,32,.18)' }}>
                {t('dashboard.reviewTours')}
              </NavLink>
            )}
            {overdueInvoices.length > 0 && (
              <NavLink to={`${base}/invoices`}
                className="text-[12px] font-bold text-[#92400E] px-3 py-1.5 rounded-[8px] transition whitespace-nowrap"
                style={{ background: 'rgba(232,160,32,.18)' }}>
                {t('dashboard.chaseInvoices')}
              </NavLink>
            )}
          </div>
        </div>
      )}

      {/* ── KPI strip ── */}
      <div className="grid grid-cols-2 1080:grid-cols-5 gap-4">
        <KpiCard
          label={t('dashboard.awaitingReview')}
          value={pendingReview.length}
          sub={t('dashboard.awaitingReviewSub')}
          sparkData={sparks.awaiting}
          sparkColor="#C0392B"
          trend={{ dir: pendingReview.length > 0 ? 'up' : 'down', value: `${pendingReview.length}` }}
          alert={pendingReview.length > 0}
          to={`${base}/requests`}
        />
        <KpiCard
          label={t('dashboard.departures30d')}
          value={upcomingDepartures.length}
          sub={t('dashboard.departures30dSub')}
          sparkData={sparks.departs}
          sparkColor="#0F7B72"
          trend={{ dir: 'up', value: `${upcomingDepartures.length}` }}
          to={`${base}/calendar`}
        />
        <KpiCard
          label={t('dashboard.activeTours')}
          value={toursInProgress.length}
          sub={t('dashboard.activeToursSub')}
          sparkData={sparks.active}
          sparkColor="#1A4A8A"
          trend={{ dir: 'up', value: `${toursInProgress.length}` }}
          to={`${base}/requests`}
        />
        <KpiCard
          label={t('dashboard.completedTours')}
          value={completedTours.length}
          sub={t('dashboard.completedToursSub')}
          to={`${base}/requests`}
        />
        <KpiCard
          label={t('dashboard.outstanding')}
          value={fmtMoneyShort(outstandingGBP)}
          sub={`${unpaidInvoices.length} invoice${unpaidInvoices.length !== 1 ? 's' : ''}${overdueInvoices.length > 0 ? ` · ${overdueInvoices.length} overdue` : ''}`}
          sparkData={sparks.outstanding}
          sparkColor="#E8A020"
          trend={{ dir: 'down', value: `${overdueInvoices.length} due` }}
          alert={overdueInvoices.length > 0}
          to={`${base}/invoices`}
        />
      </div>

      {/* ── Row: Revenue + Pipeline ── */}
      <div className="grid grid-cols-1 1080:grid-cols-[1.65fr_1fr] gap-[18px]">
        <Card
          title={t('dashboard.revenue')}
          sub={`Invoiced vs collected · GBP · ${new Date().getFullYear()}`}
          action={<Segment options={['Year','Quarter']} value="Year" onChange={() => {}} labelFor={(o) => t(`dashboard.period${o}`)} />}
        >
          <RevenueChart data={revenueData} />
        </Card>
        <Card title={t('dashboard.tourPipeline')} sub={t('dashboard.pipelineSub')} action={<ViewAll to={`${base}/requests`} />}>
          <PipelineFunnel data={pipelineData} colors={CATEGORY_COLORS} labels={Object.fromEntries(Object.keys(CATEGORY_LABELS).map((k) => [k, t(`category.${k}`)]))} />
        </Card>
      </div>

      {/* ── Row: Tours in Progress + Needs Attention ── */}
      <div className="grid grid-cols-1 1080:grid-cols-[1.65fr_1fr] gap-[18px]">

        {/* Tours in Progress */}
        <Card
          title={t('dashboard.toursInProgress')}
          sub={`${toursInProgress.length} tour${toursInProgress.length !== 1 ? 's' : ''} ${t('dashboard.activeToursSub')}`}
          action={<ViewAll to={`${base}/requests`} />}
          noPad
        >
          {toursInProgress.length === 0 ? (
            <div className="px-[18px] py-10 text-center text-[13px] text-[#9CA3AF]">No active tours right now.</div>
          ) : (
            <div className="divide-y divide-[#F1F3F6]">
              {toursInProgress.slice(0, 6).map((req) => {
                const cat = req.category || 'sales_admin';
                const color = CATEGORY_COLORS[cat] || '#1A4A8A';
                const fill = CATEGORY_FILLS[cat] || '#EBF2FC';
                const dl = daysUntil(req.departureDate);
                const stages = stagesFor(cat, req);
                const stageDone = stages.filter((s) => req.stageProgress?.[s.key]).length;
                return (
                  <button
                    key={req.id}
                    onClick={() => navigate(`${base}/requests/${req.id}`)}
                    className="w-full flex items-center gap-3 px-[18px] py-[12px] text-left hover:bg-surface transition-colors"
                  >
                    <span className="w-[36px] h-[36px] rounded-[10px] flex items-center justify-center flex-shrink-0"
                      style={{ background: fill, color }}>
                      <SportIcon sport={req.sport} className="w-[18px] h-[18px]" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="text-[13px] font-bold text-[#111827] truncate">
                          {req.internalRef || req.tourName || req.sport}
                        </span>
                        {req.brand === 'your_tour' && (
                          <span className="text-[9.5px] font-bold px-1.5 py-0.5 rounded-[4px] bg-pb text-white flex-shrink-0">YT</span>
                        )}
                        {req.brand === 'padelante' && (
                          <span className="text-[9.5px] font-bold px-1.5 py-0.5 rounded-[4px] bg-[#8DC63F] text-[#1B4079] flex-shrink-0">PAD</span>
                        )}
                      </div>
                      <div className="text-[11px] text-[#9CA3AF] mt-[2px] truncate">
                        {req.destination && <>{req.destination} · </>}{totalTravellers(req)} pax · {fmtShortDate(req.departureDate)}–{fmtShortDate(req.returnDate)}
                      </div>
                      <div className="flex items-center gap-2 mt-[5px]">
                        <CategoryChip catKey={cat} />
                        {stages.length > 0 && <ProgressRing done={stageDone} total={stages.length} color={color} size={26} />}
                      </div>
                    </div>
                    {dl !== null && (
                      <span className={`text-[11px] font-bold px-[7px] py-[3px] rounded-[20px] flex-shrink-0 self-start mt-[2px] ${
                        dl <= 14 ? 'bg-amber-light text-[#92400E]' : 'bg-surface text-[#6B7280]'
                      }`}>{dl}d</span>
                    )}
                  </button>
                );
              })}
              {toursInProgress.length > 6 && (
                <div className="px-[18px] py-[10px]">
                  <NavLink to={`${base}/requests`} className="text-[12px] text-pb hover:text-navy font-semibold transition">
                    +{toursInProgress.length - 6} more tours →
                  </NavLink>
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Right column: Invoices Due + Next Departures */}
        <div className="flex flex-col gap-[18px]">

          {/* Invoices Due */}
          <Card title={t('dashboard.invoicesDue')} sub={`${fmtMoneyShort(outstandingGBP)} ${t('dashboard.outstandingSuffix')}`} action={<ViewAll to={`${base}/invoices`} />} noPad>
            {unpaidInvoices.length === 0 ? (
              <div className="px-[18px] py-10 text-center text-[13px] text-[#9CA3AF]">{t('dashboard.noOutstanding')}</div>
            ) : (
              <div className="divide-y divide-[#F1F3F6]">
                {unpaidInvoices.slice(0, 5).map((inv) => {
                  const dl = inv.dueDate ? Math.round((new Date(inv.dueDate) - today) / 864e5) : null;
                  const overdue = dl !== null && dl < 0;
                  const soon = dl !== null && dl >= 0 && dl <= 7;
                  const sym = CURRENCY_SYMBOLS[inv.currency] || '£';
                  return (
                    <div key={inv.id} className="flex items-center gap-3 px-[18px] py-[12px]">
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] font-bold text-[#111827] truncate">{(() => { const r = requestsById[inv.requestId]; return r ? (r.internalRef || r.tourName || [r.sport, r.destination].filter(Boolean).join(' — ')) : (inv.requestSummary || '—'); })()}</div>
                        <div className="flex items-center gap-[6px] mt-[3px]">
                          <span className={`text-[11px] font-bold px-[7px] py-[2px] rounded-[20px] ${
                            overdue ? 'bg-red-light text-dr' : soon ? 'bg-amber-light text-[#92400E]' : 'bg-surface text-[#6B7280]'
                          }`}>
                            {overdue ? `Overdue ${-dl}d` : soon ? `Due in ${dl}d` : `Due ${formatDate(inv.dueDate)}`}
                          </span>
                          {inv.installmentLabel && <span className="text-[11px] text-[#9CA3AF]">{inv.installmentLabel}</span>}
                        </div>
                      </div>
                      <span className={`text-[13.5px] font-extrabold tabular-nums flex-shrink-0 ${overdue ? 'text-dr' : 'text-[#111827]'}`}>
                        {fmtMoney(inv.amount, sym)}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          {/* Next Departures */}
          <Card title={t('dashboard.nextDepartures')} sub={t('dashboard.nextDeparturesSub')} action={<ViewAll to={`${base}/calendar`} />} noPad>
            {upcomingDepartures.length === 0 ? (
              <div className="px-[18px] py-8 text-center text-[13px] text-[#9CA3AF]">No departures in the next 30 days.</div>
            ) : (
              <div className="divide-y divide-[#F1F3F6]">
                {upcomingDepartures.slice(0, 4).map((req) => {
                  const cat = req.category || 'sales_admin';
                  const color = CATEGORY_COLORS[cat] || '#1A4A8A';
                  const dl = daysUntil(req.departureDate);
                  return (
                    <button
                      key={req.id}
                      onClick={() => navigate(`${base}/requests/${req.id}`)}
                      className="w-full flex items-center gap-3 px-[18px] py-[12px] text-left hover:bg-surface transition-colors"
                    >
                      <span className={`w-[44px] h-[42px] rounded-[8px] flex flex-col items-center justify-center flex-shrink-0 ${
                        dl <= 14 ? 'bg-amber-light' : 'bg-surface'
                      }`}>
                        <b className="text-[17px] font-extrabold text-navy leading-none tabular-nums">{dl}</b>
                        <span className="text-[9px] font-semibold text-[#6B7280]">days</span>
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] font-bold text-[#111827]">
                          {req.internalRef || req.tourName || `${req.sport} · ${req.destination?.split(',')[0]}`}
                        </div>
                        <div className="text-[11.5px] text-[#9CA3AF] mt-0.5">
                          {req.groupType} · {fmtShortDate(req.departureDate)}
                        </div>
                      </div>
                      <span className="w-[7px] h-[7px] rounded-full flex-shrink-0" style={{ background: color }} />
                    </button>
                  );
                })}
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* ── Operations Workload ── */}
      <Card
        title={t('dashboard.operationsWorkload')}
        sub={`${totalIncompleteTasks} open task${totalIncompleteTasks !== 1 ? 's' : ''} across ${requestsWithTasks.length} active tour${requestsWithTasks.length !== 1 ? 's' : ''}`}
      >
        {loadingTasks ? (
          <p className="text-[13px] text-[#9CA3AF]">Loading tasks…</p>
        ) : tasksError ? (
          <p className="text-[13px] text-[#9CA3AF]">Could not load tasks — check the browser console for a Firestore index link.</p>
        ) : workloadData.length === 0 ? (
          <p className="text-[13px] text-[#9CA3AF]">No open tasks across active tours.</p>
        ) : (
          <div className="flex flex-wrap gap-x-[32px] gap-y-4">
            <div className="flex-1 min-w-[240px]">
              <WorkloadBar data={workloadData} colors={CATEGORY_COLORS} shortLabels={CATEGORY_SHORT} />
            </div>
            {requestsWithTasks.length > 0 && (
              <>
                <div className="w-px bg-[#EDEFF2] self-stretch hidden 1080:block" />
                <div className="flex flex-col gap-[10px] min-w-[160px]">
                  {requestsWithTasks.slice(0, 4).map(({ requestId, request, tasks: taskList }) => {
                    const cat = request?.category || 'sales_admin';
                    const color = CATEGORY_COLORS[cat] || '#9CA3AF';
                    return (
                      <button
                        key={requestId}
                        onClick={() => navigate(`${base}/requests/${requestId}`)}
                        className="flex items-center gap-[6px] text-left hover:opacity-80 transition"
                      >
                        <span className="w-[6px] h-[6px] rounded-full flex-shrink-0" style={{ background: color }} />
                        <span className="text-[12px] font-semibold text-[#374151] truncate">
                          {request.sport} — {request.destination?.split(',')[0]}
                        </span>
                        <span className="ml-auto text-[11px] font-bold text-[#92400E] bg-amber-light px-[7px] py-[2px] rounded-[20px] flex-shrink-0">
                          {taskList.length}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
